import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Search, CheckCircle, Clock, FileText, Send, AlertCircle, Loader2, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useNoIndex } from '../lib/seoHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status configuration with icons, colors, and descriptions
const STATUS_CONFIG = {
  submitted: {
    label: 'Submitted',
    icon: Send,
    color: 'bg-blue-500',
    textColor: 'text-blue-400',
    description: 'Your case has been received and is in our queue for review.'
  },
  under_review: {
    label: 'Under Review',
    icon: Search,
    color: 'bg-yellow-500',
    textColor: 'text-yellow-400',
    description: 'Our team is reviewing your case details and gathering information.'
  },
  documentation_in_progress: {
    label: 'Documentation In Progress',
    icon: FileText,
    color: 'bg-orange-500',
    textColor: 'text-orange-400',
    description: 'We are preparing your documentation.'
  },
  documentation_complete: {
    label: 'Documentation Complete',
    icon: CheckCircle,
    color: 'bg-lime-500',
    textColor: 'text-lime-400',
    description: 'Your dispute documentation has been completed and is ready for submission.'
  },
  sent_to_platform: {
    label: 'Sent to Platform',
    icon: Send,
    color: 'bg-purple-500',
    textColor: 'text-purple-400',
    description: 'Your documentation has been prepared for submission through official appeal channels.'
  },
  awaiting_response: {
    label: 'Awaiting Response',
    icon: Clock,
    color: 'bg-indigo-500',
    textColor: 'text-indigo-400',
    description: 'Waiting for a response from the platform. This typically takes 7-21 days.'
  },
  response_received: {
    label: 'Response Received',
    icon: AlertCircle,
    color: 'bg-cyan-500',
    textColor: 'text-cyan-400',
    description: 'We have received a response from the platform and are reviewing it.'
  },
  resolved: {
    label: 'Resolved',
    icon: CheckCircle,
    color: 'bg-green-500',
    textColor: 'text-green-400',
    description: 'Your case documentation has been completed.'
  },
  denied: {
    label: 'Denied',
    icon: AlertCircle,
    color: 'bg-red-500',
    textColor: 'text-red-400',
    description: 'The platform denied the request. Contact us to discuss next steps.'
  },
  closed: {
    label: 'Closed',
    icon: CheckCircle,
    color: 'bg-gray-500',
    textColor: 'text-gray-400',
    description: 'This case has been closed.'
  }
};

// Progress steps for the timeline
const PROGRESS_STEPS = [
  'submitted',
  'under_review',
  'documentation_in_progress',
  'documentation_complete',
  'sent_to_platform',
  'awaiting_response',
  'resolved'
];

const CaseTrackerPage = () => {
  useNoIndex();
  const [searchParams] = useSearchParams();
  const initialCaseId = searchParams.get('id') || '';
  
  const [caseId, setCaseId] = useState(initialCaseId);
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!caseId.trim()) {
      toast.error('Please enter your Case ID');
      return;
    }
    
    setLoading(true);
    setError(null);
    setCaseData(null);
    
    try {
      const response = await axios.get(`${API}/track/${caseId.trim()}`);
      setCaseData(response.data);
      toast.success('Case found!');
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Case not found. Please check your Case ID and try again.');
      } else {
        setError('An error occurred. Please try again later.');
      }
      toast.error('Case not found');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(caseData?.caseId);
    setCopied(true);
    toast.success('Case ID copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const getStatusIndex = (status) => {
    return PROGRESS_STEPS.indexOf(status);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const currentStatus = caseData?.status || 'submitted';
  const statusConfig = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.submitted;
  const StatusIcon = statusConfig.icon;

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3f4816]/30 rounded-full mb-6">
              <Search className="w-5 h-5 text-[#d9fb06]" />
              <span className="text-sm font-medium text-[#d9fb06] uppercase tracking-wide">Case Tracker</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-[#d9fb06] uppercase leading-[0.9] mb-6">
              Track Your Case
            </h1>
            <p className="text-lg text-[#888680] max-w-2xl mx-auto">
              Enter your Case ID to check the status of your dispute. Your Case ID was provided in your confirmation email.
            </p>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="mb-12">
            <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-2xl p-6 md:p-8">
              <label className="block text-[#dfddd6] font-medium mb-3">
                Enter Your Case ID
              </label>
              <div className="flex gap-3">
                <Input
                  type="text"
                  value={caseId}
                  onChange={(e) => setCaseId(e.target.value.toUpperCase())}
                  placeholder="SDC-20250207-A1B2C3"
                  className="bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] focus:border-[#d9fb06] font-mono text-lg"
                  data-testid="case-id-input"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 font-bold px-8"
                  data-testid="track-case-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Search className="w-5 h-5" />
                  )}
                </Button>
              </div>
              <p className="text-sm text-[#888680] mt-3">
                Your Case ID looks like: <span className="font-mono text-[#d9fb06]">SDC-YYYYMMDD-XXXXXX</span>
              </p>
            </div>
          </form>

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 mb-8 text-center">
              <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
              <p className="text-red-400">{error}</p>
              <p className="text-[#888680] text-sm mt-2">
                If you believe this is an error, please contact us at{' '}
                <a href="mailto:intake@socialdisputeco.com" className="text-[#d9fb06] hover:underline">
                  intake@socialdisputeco.com
                </a>
              </p>
            </div>
          )}

          {/* Case Status Result */}
          {caseData && (
            <div className="space-y-6" data-testid="case-result">
              
              {/* Case Overview Card */}
              <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-2xl p-6 md:p-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                  <div>
                    <p className="text-sm text-[#888680] uppercase tracking-wide mb-1">Case ID</p>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-mono font-bold text-[#d9fb06]" data-testid="case-id-display">
                        {caseData.caseId}
                      </span>
                      <button
                        onClick={copyToClipboard}
                        className="p-2 rounded-lg bg-[#3f4816]/30 text-[#888680] hover:text-[#d9fb06] transition-colors"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${statusConfig.color}/20`}>
                    <StatusIcon className={`w-5 h-5 ${statusConfig.textColor}`} />
                    <span className={`font-bold uppercase text-sm ${statusConfig.textColor}`}>
                      {statusConfig.label}
                    </span>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-[#888680] mb-1">Client</p>
                    <p className="text-[#dfddd6] font-medium">{caseData.clientName}</p>
                  </div>
                  <div>
                    <p className="text-[#888680] mb-1">Platform</p>
                    <p className="text-[#dfddd6] font-medium">{caseData.platform}</p>
                  </div>
                  <div>
                    <p className="text-[#888680] mb-1">Submitted</p>
                    <p className="text-[#dfddd6] font-medium">{formatDate(caseData.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Current Status Description */}
              <div className={`bg-[#1a1c1b] border-l-4 ${statusConfig.color} rounded-r-xl p-6`}>
                <div className="flex items-start gap-4">
                  <StatusIcon className={`w-8 h-8 ${statusConfig.textColor} flex-shrink-0`} />
                  <div>
                    <h3 className={`text-lg font-bold ${statusConfig.textColor} mb-2`}>
                      {statusConfig.label}
                    </h3>
                    <p className="text-[#888680]">{statusConfig.description}</p>
                  </div>
                </div>
              </div>

              {/* Progress Timeline */}
              <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-2xl p-6 md:p-8">
                <h3 className="text-lg font-bold text-[#d9fb06] uppercase mb-6">Progress Timeline</h3>
                
                <div className="space-y-4">
                  {caseData.statusHistory?.map((entry, index) => {
                    const entryConfig = STATUS_CONFIG[entry.status] || STATUS_CONFIG.submitted;
                    const EntryIcon = entryConfig.icon;
                    
                    return (
                      <div 
                        key={`${entry.timestamp || 'entry'}-${entry.status}-${index}`} 
                        className="flex items-start gap-4 relative"
                      >
                        {/* Timeline line */}
                        {index < caseData.statusHistory.length - 1 && (
                          <div className="absolute left-4 top-10 w-0.5 h-full bg-[#3f4816]" />
                        )}
                        
                        {/* Icon */}
                        <div className={`w-8 h-8 rounded-full ${entryConfig.color} flex items-center justify-center flex-shrink-0 relative z-10`}>
                          <EntryIcon className="w-4 h-4 text-white" />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 pb-4">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                            <span className={`font-bold ${entryConfig.textColor}`}>
                              {entryConfig.label}
                            </span>
                            <span className="text-xs text-[#888680]">
                              {formatDate(entry.timestamp)}
                            </span>
                          </div>
                          {entry.note && (
                            <p className="text-sm text-[#888680] mt-1">{entry.note}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Contact Section */}
              <div className="bg-[#1a1c1b] border border-[#3f4816]/30 rounded-xl p-6 text-center">
                <p className="text-[#888680] mb-4">
                  Questions about your case? Contact us:
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="mailto:intake@socialdisputeco.com"
                    className="inline-flex items-center justify-center gap-2 text-[#d9fb06] hover:underline"
                  >
                    intake@socialdisputeco.com
                  </a>
                  <span className="hidden sm:inline text-[#3f4816]">|</span>
                  <a
                    href="tel:808-301-2134"
                    className="inline-flex items-center justify-center gap-2 text-[#d9fb06] hover:underline"
                  >
                    808-301-2134
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Empty State - Before Search */}
          {!caseData && !error && !loading && (
            <div className="text-center py-12">
              <div className="w-20 h-20 rounded-full bg-[#302f2c] flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-[#3f4816]" />
              </div>
              <h3 className="text-xl font-bold text-[#dfddd6] mb-2">
                Enter your Case ID above
              </h3>
              <p className="text-[#888680] max-w-md mx-auto">
                Your Case ID was sent to you in your confirmation email after submitting your intake form.
              </p>
            </div>
          )}

        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CaseTrackerPage;
